/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useAsyncData } from "./useAsyncData";
import * as drive from "../../../api/drive";
import { downloadFile } from "./useDrive";
import { formatBytes, formatDate } from "./format";

/** Flat, most-recent-first view across every folder. */
// Module scope keeps the reference stable for useAsyncData.
const fetchRecent = () => drive.listRecentFiles({ pageSize: 50 }).then((files) => ({ files }));

const RecentPage = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const state = useAsyncData(fetchRecent, { files: [] }, "Could not load recent files.");


  const download = async (file) => {
    try {
      await downloadFile(file.id, file.file_name);
    } catch (err) {
      enqueueSnackbar(err.message || "Download failed.", { variant: "error" });
    }
  };

  if (state.loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: "accent.main" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography sx={{ fontSize: 20, fontWeight: 600, mb: 2 }}>Recent files</Typography>

      {state.error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={state.reload}>Retry</Button>}>
          {state.error}
        </Alert>
      )}

      <Paper variant="outlined">
        {state.files.length === 0 ? (
          <Typography sx={{ p: 3, fontSize: 13.5, color: "secondaryText.color" }}>
            Nothing here yet. Files you upload will show up in this list.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell width={120}>Size</TableCell>
                <TableCell width={140}>Modified</TableCell>
                <TableCell width={110} />
              </TableRow>
            </TableHead>
            <TableBody>
              {state.files.map((file) => (
                <TableRow key={file.id} hover>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <InsertDriveFileIcon
                        fontSize="small"
                        sx={{ color: "text.color", opacity: 0.6 }}
                      />
                      <Typography variant="body2" noWrap>{file.file_name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{formatBytes(file.size_bytes)}</TableCell>
                  <TableCell>{formatDate(file.updated_at)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Open containing folder">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(file.folder_id ? `/drive/folders/${file.folder_id}` : "/drive")
                        }
                      >
                        <FolderOpenOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download">
                      <IconButton size="small" onClick={() => download(file)}>
                        <DownloadOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
};

export default RecentPage;
